import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { NavLink } from "@/components/NavLink";
import {
  BookOpen,
  Search,
  Users,
  ArrowLeftRight,
  List,
  PenSquare,
  Info,
  FileText,
  IdCard,
  ArrowDownNarrowWide,
  LayoutGrid,
  Link,
  RefreshCw,
  Globe,
  Clock,
  Megaphone,
  GraduationCap,
  Tag,
  Sparkles,
  Presentation,
  Share2,
  ArrowLeft,
} from "lucide-react";

const allFeatures = [
  {
    category: "Devocional Pessoal",
    description: "",
    features: [
      {
        icon: BookOpen,
        title: "Guia na Devocional Diária",
        description: "Crie um guia devocional que una reflexão bíblica, aplicação prática e crescimento espiritual.",
      },
    ],
  },
  {
    category: "Investigando Textos (Exegese)",
    description: "Foco na análise detalhada da passagem bíblica em seu contexto original.",
    features: [
      {
        icon: Search,
        title: "Analisar Palavras",
        description: "Analise termos bíblicos revelando sentidos originais, contexto teológico e nuances de significado.",
      },
      {
        icon: Users,
        title: "Caracterizar Personagens",
        description: "Elabore perfis detalhados de personagens destacando sua trajetória e relevância teológica.",
      },
      {
        icon: ArrowLeftRight,
        title: "Comparar Traduções",
        description: "Compare diferentes traduções bíblicas, evidenciando variações linguísticas e implicações interpretativas.",
      },
      {
        icon: List,
        title: "Delimitar Unidades",
        description: "Identifique unidades textuais, analisando sua estrutura, coesão e função dentro da passagem.",
      },
      {
        icon: PenSquare,
        title: "Identificar Forma Literária",
        description: "Reconheça a forma literária de textos bíblicos e seus efeitos teológicos e narrativos.",
      },
      {
        icon: Info,
        title: "Pesquisar Contexto",
        description: "Pesquise informações históricas, culturais e literárias que iluminem o sentido original do texto.",
      },
    ],
  },
  {
    category: "Mapeando Temas (Teologia Bíblica)",
    description: "Foco na conexão da passagem com as grandes narrativas bíblicas.",
    features: [
      {
        icon: FileText,
        title: "Apresentar Teologia de um Livro",
        description: "Sintetize a teologia de um livro bíblico, revelando seus temas e eixos centrais.",
      },
      {
        icon: IdCard,
        title: "Biografar Personagens",
        description: "Crie biografias teológicas que explorem a fé, o caráter e a missão dos personagens.",
      },
      {
        icon: ArrowDownNarrowWide,
        title: "Descrever Teologias",
        description: "Descreva as principais teologias da Bíblia, conectando teologia, história e espiritualidade.",
      },
      {
        icon: LayoutGrid,
        title: "Explorar Temas",
        description: "Explore temas contemporâneos relevantes conectando-os à mensagem bíblica integral.",
      },
      {
        icon: Link,
        title: "Relacionar Textos",
        description: "Relacione passagens bíblicas, revelando continuidade e unidade nos temas teológicos.",
      },
    ],
  },
  {
    category: "Explorando Significados (Hermenêutica)",
    description: "Foco na construção de pontes entre o mundo bíblico e o contemporâneo.",
    features: [
      {
        icon: RefreshCw,
        title: "Atualizar Texto",
        description: "Atualize textos bíblicos para uma linguagem contemporânea sem perder precisão e reverência.",
      },
      {
        icon: Globe,
        title: "Examinar Cosmovisão",
        description: "Examine a cosmovisão expressa em uma passagem e seus reflexos na teologia e cultura.",
      },
      {
        icon: Clock,
        title: "Historiar Interpretação",
        description: "Pesquise como uma passagem foi interpretada ao longo da história da fé cristã.",
      },
    ],
  },
  {
    category: "Preparando Mensagem (Homilética)",
    description: "Foco nas práticas para comunicar o que foi estudado.",
    features: [
      {
        icon: Megaphone,
        title: "Construir Sermão",
        description: "Construa sermões sólidos unindo exegese bíblica, teologia profunda e aplicação prática.",
      },
      {
        icon: GraduationCap,
        title: "Criar Estudo Bíblico",
        description: "Crie estudos bíblicos completos com base textual, perguntas e reflexões orientadas.",
      },
      {
        icon: Tag,
        title: "Definir Público-Alvo",
        description: "Defina o público de forma precisa e direcione a comunicação conforme o contexto ministerial.",
      },
      {
        icon: Sparkles,
        title: "Gerar Ilustrações",
        description: "Gere analogias e histórias que tornem a mensagem bíblica clara, viva e memorável.",
      },
      {
        icon: Presentation,
        title: "Preparar Aula",
        description: "Prepare planos de aula bíblica didáticos e envolventes para diferentes faixas etárias.",
      },
      {
        icon: Share2,
        title: "Sugerir Posts",
        description: "Receba ideias de posts com conteúdo bíblico relevante e linguagem acessível para redes sociais.",
      },
    ],
  },
];

const AllFeatures = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <NavLink to="/">
              <Button variant="outline" size="sm" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </Button>
            </NavLink>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <NavLink to="/">Home</NavLink>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>Recursos</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          
          <div className="text-center mb-16 space-y-6">
            <h1 className="text-4xl md:text-5xl font-serif font-bold">
              <span className="text-foreground">Todas as </span>
              <span className="text-secondary">Funcionalidades</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Conheça todas as equipes de Agentes Inteligentes da EscriturIA que auxiliam você no estudo e preparação bíblica.
            </p>
          </div>

          <div className="space-y-16">
            {allFeatures.map((category, categoryIndex) => (
              <div key={categoryIndex} className="space-y-6">
                <div className="space-y-2 text-center">
                  <h2 className="text-3xl font-serif font-bold text-foreground">{category.category}</h2>
                  {category.description && (
                    <p className="text-lg text-muted-foreground">{category.description}</p>
                  )}
                </div>
                <div className={category.features.length === 1 ? 'flex justify-center' : 'grid gap-6 md:grid-cols-2 lg:grid-cols-3'}>
                  {category.features.map((feature, index) => {
                    const Icon = feature.icon;
                    return (
                      <Card
                        key={index}
                        className={`border hover:border-secondary transition-all duration-300 hover:shadow-lg hover:bg-secondary/5 group ${category.features.length === 1 ? 'w-full max-w-md' : ''}`}
                      >
                        <CardContent className="p-6 space-y-4">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/30 transition-colors flex-shrink-0">
                              <Icon className="w-6 h-6 text-secondary" />
                            </div>
                            <div className="space-y-2">
                              <h3 className="text-lg font-semibold text-foreground leading-tight">{feature.title}</h3>
                              <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AllFeatures;
